import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';
import { HiArrowTrendingUp } from 'react-icons/hi2';
import { GiGears } from 'react-icons/gi';
import { exploreUnknownXpBarPercent } from './utils/exploreUnknownXpBarPercent';
import './ExploreUnknownLevelTree.css';

const MILESTONES = [
  5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100,
];

/** Milestone `bonusId` values that show the green up-arrow badge (see `exploreUnknownLeveling.json`). */
const MILESTONE_UP_ARROW_BONUS_IDS = new Set([
  'memoryBonusDarkness',
  'heartHealthBonus',
  'epFewerFragments',
  'rewriteFewerFragments',
  'singleFragment',
]);

/** Milestone `bonusId` values for auto-collect token bonuses (white cog badge). */
const MILESTONE_AUTO_COLLECT_BONUS_IDS = new Set([
  'autoCollectMemory',
  'autoCollectDarkKnowledge',
  'autoCollectRewrite',
  'autoCollectEp',
]);

/** Levels 50 & 55 — health-related bonuses (green glowing plus signs). */
const MILESTONE_HEALTH_PLUS_BONUS_IDS = new Set(['darknessReduction', 'healthPerWin']);

/** Corner badges (arrow / cogs / pluses) only appear once this milestone level is reached. */
const MILESTONE_SPECIAL_CORNER_MIN_LEVEL = 50;

const MILESTONE_CORNER_ICON_SIZE_PX = 20;
const MILESTONE_HEALTH_PLUS_ICON_SIZE_PX = 11;

const COLS = 5;
const ROWS = 4;

const TILE = 104;
const GAP_X = 44;
const GAP_Y = 56;
const PAD_X = 28;
const PAD_Y = 36;

const BOARD_W = PAD_X * 2 + COLS * TILE + (COLS - 1) * GAP_X;
const BOARD_H = PAD_Y * 2 + ROWS * TILE + (ROWS - 1) * GAP_Y;

function milestoneCenter(index) {
  const col = Math.floor(index / ROWS);
  const within = index % ROWS;
  const row = col % 2 === 0 ? within : ROWS - 1 - within;
  const x = PAD_X + col * (TILE + GAP_X) + TILE / 2;
  const y = PAD_Y + row * (TILE + GAP_Y) + TILE / 2;
  return { col, row, x, y };
}

function buildSnakePath(points) {
  const parts = [];
  points.forEach((p, i) => {
    if (i === 0) {
      parts.push(`M ${p.x} ${p.y}`);
      return;
    }
    const prev = points[i - 1];
    if (prev.x === p.x) {
      parts.push(`L ${p.x} ${p.y}`);
    } else {
      const midY = (prev.y + p.y) / 2;
      parts.push(`C ${prev.x} ${midY}, ${p.x} ${midY}, ${p.x} ${p.y}`);
    }
  });
  return parts.join(' ');
}

export default function ExploreUnknownLevelTree({
  level,
  xpIntoLevel,
  xpToNext,
  totalXp,
  milestoneBonuses = {},
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);

  const centers = useMemo(() => MILESTONES.map((_, i) => milestoneCenter(i)), []);
  const path = useMemo(() => buildSnakePath(centers), [centers]);

  const xpPct = exploreUnknownXpBarPercent(xpIntoLevel, xpToNext);
  const totalXpDisplay = Number(totalXp || 0).toLocaleString();

  const lastUnlockedIdx = MILESTONES.reduce(
    (acc, ml, i) => (level >= ml ? i : acc),
    -1
  );
  const nextMilestoneIdx = lastUnlockedIdx + 1 < MILESTONES.length ? lastUnlockedIdx + 1 : -1;

  return (
    <div className="eu-level-tree" aria-label={t('exploreUnknownPage.levelTree.regionLabel')}>
      <div className="eu-level-tree-header">
        <div className="eu-level-tree-level-badge">
          <span className="eu-level-tree-level-badge-label">LV</span>
          <span className="eu-level-tree-level-badge-value">{level}</span>
        </div>
        <div className="eu-level-tree-header-text">
          <span className="eu-level-tree-title">
            {t('exploreUnknownPage.levelTree.title')}
          </span>
          <span className="eu-level-tree-xp-total">
            {t('exploreUnknownPage.levelTree.totalXp', { xp: totalXpDisplay })}
          </span>
          <div
            className="eu-level-tree-bar"
            role="progressbar"
            aria-valuenow={xpIntoLevel}
            aria-valuemin={0}
            aria-valuemax={xpIntoLevel + xpToNext || xpIntoLevel}
          >
            <div className="eu-level-tree-bar-fill" style={{ width: `${xpPct}%` }} />
          </div>
          {xpToNext > 0 ? (
            <span className="eu-level-tree-xp-next">
              {t('exploreUnknownPage.levelTree.xpToNext', {
                current: Number(xpIntoLevel || 0).toLocaleString(),
                needed: Number((xpIntoLevel || 0) + (xpToNext || 0)).toLocaleString(),
              })}
            </span>
          ) : (
            <span className="eu-level-tree-xp-max">
              {t('exploreUnknownPage.levelTree.maxLevel')}
            </span>
          )}
        </div>
      </div>

      <div
        className="eu-level-tree-board"
        style={{ width: `${BOARD_W}px`, height: `${BOARD_H}px` }}
      >
        <svg
          className="eu-level-tree-path-svg"
          width={BOARD_W}
          height={BOARD_H}
          viewBox={`0 0 ${BOARD_W} ${BOARD_H}`}
          aria-hidden
        >
          <defs>
            <linearGradient id="eu-tree-path-locked" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(148, 163, 184, 0.35)" />
              <stop offset="100%" stopColor="rgba(71, 85, 105, 0.25)" />
            </linearGradient>
            <linearGradient id="eu-tree-path-unlocked" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fde68a" />
              <stop offset="50%" stopColor="#facc15" />
              <stop offset="100%" stopColor="#f59e0b" />
            </linearGradient>
          </defs>
          <path
            d={path}
            fill="none"
            stroke="url(#eu-tree-path-locked)"
            strokeWidth="10"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="2 8"
          />
          {lastUnlockedIdx >= 0 ? (
            <path
              d={buildSnakePath(centers.slice(0, lastUnlockedIdx + 1))}
              fill="none"
              stroke="url(#eu-tree-path-unlocked)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="eu-level-tree-path-unlocked"
            />
          ) : null}
        </svg>

        {MILESTONES.map((ml, i) => {
          const { x, y } = centers[i];
          const unlocked = level >= ml;
          const isNext = i === nextMilestoneIdx;
          const meta = milestoneBonuses[String(ml)] || {};
          const showSecret = !unlocked;
          const showSpecialCornerIcons =
            unlocked && ml >= MILESTONE_SPECIAL_CORNER_MIN_LEVEL;
          const title = meta.i18nKey
            ? t(`exploreUnknownPage.${meta.i18nKey}.title`)
            : '';
          const description = meta.i18nKey
            ? t(`exploreUnknownPage.${meta.i18nKey}.description`)
            : '';
          const tooltipSide = x > BOARD_W / 2 ? 'left' : 'right';
          return (
            <div
              key={ml}
              className={`eu-level-tree-tile${unlocked ? ' eu-level-tree-tile--unlocked' : ''}${!unlocked && ml >= MILESTONE_SPECIAL_CORNER_MIN_LEVEL ? ' eu-level-tree-tile--special-locked' : ''}${isNext ? ' eu-level-tree-tile--next' : ''}`}
              style={{
                left: `${x - TILE / 2}px`,
                top: `${y - TILE / 2}px`,
                width: `${TILE}px`,
                height: `${TILE}px`,
              }}
              onMouseEnter={() => setHovered(ml)}
              onMouseLeave={() => setHovered((cur) => (cur === ml ? null : cur))}
              onFocus={() => setHovered(ml)}
              onBlur={() => setHovered((cur) => (cur === ml ? null : cur))}
              tabIndex={0}
              role="button"
              aria-label={
                unlocked
                  ? `${title} — ${t('exploreUnknownPage.levelTree.regionLabel')} ${ml}`
                  : t('exploreUnknownPage.levelTree.secretBonus')
              }
            >
              <div className="eu-level-tree-tile-frame">
                {meta.icon ? (
                  <img
                    src={meta.icon}
                    alt=""
                    className="eu-level-tree-tile-icon"
                    draggable={false}
                  />
                ) : (
                  <div className="eu-level-tree-tile-icon eu-level-tree-tile-icon--placeholder" />
                )}
                {showSpecialCornerIcons && MILESTONE_UP_ARROW_BONUS_IDS.has(meta.bonusId) ? (
                  <span
                    className="eu-level-tree-tile-corner-badge eu-level-tree-tile-corner-badge--boost"
                    aria-hidden="true"
                  >
                    <HiArrowTrendingUp size={MILESTONE_CORNER_ICON_SIZE_PX} />
                  </span>
                ) : null}
                {showSpecialCornerIcons && MILESTONE_AUTO_COLLECT_BONUS_IDS.has(meta.bonusId) ? (
                  <span
                    className="eu-level-tree-tile-corner-badge eu-level-tree-tile-corner-badge--auto"
                    aria-hidden="true"
                  >
                    <GiGears size={MILESTONE_CORNER_ICON_SIZE_PX} />
                  </span>
                ) : null}
                {showSpecialCornerIcons && MILESTONE_HEALTH_PLUS_BONUS_IDS.has(meta.bonusId) ? (
                  <span
                    className="eu-level-tree-tile-corner-badge eu-level-tree-tile-corner-badge--health"
                    aria-hidden="true"
                  >
                    <span className="eu-level-tree-tile-health-pluses">
                      <FaPlus size={MILESTONE_HEALTH_PLUS_ICON_SIZE_PX} />
                      <FaPlus size={MILESTONE_HEALTH_PLUS_ICON_SIZE_PX} />
                      <FaPlus size={MILESTONE_HEALTH_PLUS_ICON_SIZE_PX} />
                    </span>
                  </span>
                ) : null}
                {showSecret ? (
                  <div className="eu-level-tree-tile-secret">?</div>
                ) : null}
              </div>
              <div className="eu-level-tree-tile-badge">{ml}</div>
              {hovered === ml ? (
                <div
                  className={`eu-level-tree-tooltip eu-level-tree-tooltip--${tooltipSide}`}
                  role="tooltip"
                >
                  <span className="eu-level-tree-tooltip-level">
                    {t('exploreUnknownPage.levelTree.regionLabel')} · {ml}
                  </span>
                  {showSecret ? (
                    <strong>{t('exploreUnknownPage.levelTree.secretBonus')}</strong>
                  ) : (
                    <>
                      <strong>{title}</strong>
                      <span>{description}</span>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
